import { Stethoscope, UserCheck, AlertTriangle, ShieldAlert, BadgeCheck, Lock, Link2 } from "lucide-react";

// Shared disclaimer body — rendered both inside the gated /disclaimer route
// (where the user must check "I agree") and inside the public-facing modal
// triggered from the pricing FAQ. Kept here in one place so the two surfaces
// always say exactly the same thing.

const SECTIONS = [
  {
    Icon: Stethoscope,
    bg: "#EAF0F9",
    tint: "#78C0D8",
    title: "Not a medical treatment",
    body: "The content in this app is for general wellness purposes only and is not designed to diagnose, treat, cure, or prevent any mental health condition — including PTSD, anxiety disorders, depression, or trauma-related conditions. This app has not been evaluated or approved by the U.S. Food and Drug Administration (FDA).",
  },
  {
    Icon: UserCheck,
    bg: "#E9F0E8",
    tint: "#3090A8",
    title: "Consult a professional",
    body: "If you currently have or have a history of trauma, PTSD, psychosis, severe anxiety, depression, or any other mental or physical health condition, please consult a licensed mental health professional before using this app. Bilateral stimulation techniques similar to EMDR should only be used under the direct supervision of a trained and licensed clinician when addressing medical or clinical conditions.",
  },
  {
    Icon: AlertTriangle,
    bg: "#FDECEA",
    tint: "#D98880",
    title: "Re-traumatization warning",
    body: "Processing traumatic memories without the guidance of a qualified professional carries real risks — including emotional overwhelm, re-traumatization, or psychological distress. If at any point you feel overwhelmed, distressed, or unsafe, stop immediately and seek support from a licensed mental health provider or crisis service.",
  },
  {
    Icon: ShieldAlert,
    bg: "#FEF3E7",
    tint: "#B8965A",
    title: "Use at your own risk",
    body: "You are solely responsible for your use of this app and any outcomes that result from it. The developers, owners, and affiliated parties of this app assume no liability for any emotional, psychological, or physical harm arising from its use.",
  },
  {
    Icon: BadgeCheck,
    bg: "#EEF4FF",
    tint: "#6B83C4",
    title: "No guarantee of results",
    body: "While we strive to provide accurate, evidence-informed content, we make no guarantees regarding the completeness, accuracy, or currency of the information provided. Individual results will vary, and any outcomes described or suggested within the app are not typical or guaranteed.",
  },
  {
    Icon: Lock,
    bg: "#E6EEE6",
    tint: "#7A9A7E",
    title: "Privacy & data",
    body: "This app takes your privacy seriously. Any personal or health-related information you provide is handled in accordance with applicable privacy laws and regulations. We do not sell your personal data to third parties.",
  },
  {
    Icon: Link2,
    bg: "#F4F1EB",
    tint: "#9B8A6A",
    title: "Third-party links",
    body: "This app may contain links to third-party websites or resources. We have no control over the content, accuracy, or availability of those sites and assume no responsibility for any information or services they provide.",
  },
];

export const DisclaimerContent = () => (
  <div data-testid="disclaimer-content" className="pt-2">
    <p className="text-xs font-medium text-[#3090A8] uppercase tracking-[0.14em] mb-2">
      Before you begin
    </p>
    <h2 className="font-outfit font-semibold text-[1.375rem] sm:text-2xl text-[#1B2349] leading-tight mb-4">
      A wellness tool — not a medical treatment
    </h2>
    <p className="text-[#1B2349] leading-relaxed mb-8">
      This app is designed exclusively as a general wellness and relaxation
      tool. It is not a medical device, mental health treatment, or substitute
      for professional therapy. Nothing here establishes a therapeutic
      relationship or constitutes the practice of psychology or counseling.
    </p>

    <div className="space-y-5">
      {SECTIONS.map(({ Icon, bg, tint, title, body }) => (
        <div key={title} className="flex gap-4 items-start">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: bg }}
          >
            <Icon className="w-5 h-5" style={{ color: tint }} />
          </div>
          <div>
            <h3 className="font-outfit font-medium text-[1.0625rem] sm:text-base text-[#1B2349] mb-1">
              {title}
            </h3>
            <p className="text-[#4A5568] leading-relaxed">{body}</p>
          </div>
        </div>
      ))}
    </div>
  </div>
);

export default DisclaimerContent;
